import { ApiResponse } from "../models/api-response";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL as string | undefined;

export type ChatbotPageContext = "landing" | "app";

export interface ChatbotMessageRequest {
  message: string;
  session_id?: string | null;
  language_code?: string | null;
  page_context?: ChatbotPageContext;
  page_path?: string | null;
}

export interface ChatbotMessageResponse {
  session_id: string;
  reply: string;
}

function resolveChatbotMessageUrl(urlOverride?: string): string {
  const trimmed = urlOverride?.trim();
  if (trimmed) return trimmed;

  const base = String(apiServerUrl ?? "").replace(/\/$/, "");
  return base ? `${base}/api/chatbot/message` : "/api/chatbot/message";
}

function formatFastApiErrorBody(errorData: Record<string, unknown>): string {
  const detail = errorData?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) =>
        item && typeof item === "object" && item !== null && "msg" in item && typeof (item as { msg?: string }).msg === "string"
          ? (item as { msg: string }).msg
          : "",
      )
      .filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  if (typeof errorData?.message === "string" && errorData.message.trim()) {
    return errorData.message.trim();
  }
  return "";
}

/**
 * Guidance chatbot — mirrors `callApi` (fetch + credentials + JSON) but maps FastAPI `detail` for errors,
 * without changing the shared `api.service.ts`.
 */
export async function sendChatbotMessage(
  body: ChatbotMessageRequest,
  urlOverride?: string,
): Promise<ApiResponse<ChatbotMessageResponse>> {
  const url = resolveChatbotMessageUrl(urlOverride);

  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      const fromBody = formatFastApiErrorBody(errorData);
      return {
        data: undefined,
        error: fromBody || `Error: ${response.status} ${response.statusText}`,
      };
    }

    const contentType = response.headers.get("content-type");
    if (response.status === 204 || !contentType || response.headers.get("content-length") === "0") {
      return { data: undefined, error: "Empty response from chatbot." };
    }

    if (contentType.includes("application/json")) {
      const data = (await response.json()) as ChatbotMessageResponse;
      return { data, error: undefined };
    }

    const text = await response.text();
    return { data: undefined, error: text || "Unexpected response from chatbot." };
  } catch (err) {
    console.error("Chatbot API call error:", err);
    return {
      data: undefined,
      error: err instanceof Error ? err.message : "An unexpected error occurred",
    };
  }
}
